import { Link } from "react-router-dom";

export interface ArticleItem {
  id: string;
  title: string;
  summary: string;
  category: string;
  categoryName: string;
  tags: string[];
  author: string;
  date: string;
  time: string;
  readCount: number;
  hot: number;
  cover: string;
  sections: { heading: string; paragraphs: string[] }[];
}

interface Props {
  article: ArticleItem;
  variant?: "default" | "compact" | "feature";
}

function formatCount(n: number) {
  return n >= 10000 ? `${(n / 10000).toFixed(1)} 万` : `${n}`;
}

export default function ArticleCard({ article, variant = "default" }: Props) {
  if (variant === "compact") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex gap-4 py-4 border-b border-background-200 last:border-b-0 cursor-pointer"
      >
        <div className="w-24 h-20 md:w-28 md:h-20 shrink-0 rounded-md overflow-hidden bg-background-200">
          <img
            src={article.cover}
            alt={article.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="min-w-0 flex flex-col justify-between">
          <h3 className="font-heading font-semibold text-sm md:text-base text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
            {article.title}
          </h3>
          <p className="text-xs text-foreground-500 mt-2 flex items-center gap-3">
            <span>{article.date}</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" />
              {formatCount(article.readCount)}
            </span>
          </p>
        </div>
      </Link>
    );
  }

  if (variant === "feature") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group relative block rounded-lg overflow-hidden h-[360px] md:h-[440px] cursor-pointer"
      >
        <img
          src={article.cover}
          alt={article.title}
          className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-5 md:p-7">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-xs font-medium">
            <i className="ri-fire-line" />
            {article.categoryName}
          </span>
          <h3 className="mt-3 font-heading font-bold text-xl md:text-3xl text-background-50 leading-tight">
            {article.title}
          </h3>
          <p className="mt-2.5 text-sm text-background-200 line-clamp-2 max-w-2xl">
            {article.summary}
          </p>
        </div>
      </Link>
    );
  }

  return (
    <article className="group flex flex-col h-full rounded-lg overflow-hidden border border-background-200 bg-background-50 hover:border-primary-300 transition-colors">
      <Link to={`/article/${article.id}`} className="block cursor-pointer">
        <div className="h-44 overflow-hidden bg-background-200">
          <img
            src={article.cover}
            alt={article.title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
      </Link>
      <div className="p-4 md:p-5 flex flex-col flex-1">
        <div className="flex items-center gap-2 text-xs">
          <span className="px-2 py-0.5 rounded-md bg-accent-100 text-accent-900 font-medium">
            {article.categoryName}
          </span>
          <span className="text-foreground-500">{article.date}</span>
        </div>
        <h3 className="mt-3 font-heading font-semibold text-base md:text-lg text-foreground-950 leading-snug line-clamp-2">
          <Link to={`/article/${article.id}`} className="hover:text-primary-600 transition-colors cursor-pointer">
            {article.title}
          </Link>
        </h3>
        <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-3 flex-1">
          {article.summary}
        </p>
        <div className="mt-4 pt-3 border-t border-background-200 flex items-center justify-between text-xs text-foreground-500">
          <span className="flex items-center gap-1.5">
            <i className="ri-user-line" />
            {article.author}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-eye-line" />
            {formatCount(article.readCount)}
          </span>
        </div>
      </div>
    </article>
  );
}